import { lazy, string, object, mixed, array, number, ref, date } from "yup";
import { colorCodeRegex } from "./common-service";
import { localToInstant } from "./business-time";
import {
  QUESTION_TYPE,
  FUNNEL_DESIGNS,
  FUNNEL_TYPE_FILTER,
  PARTNER_LOGO_MODE,
  PARTNER_LOGO_SCALE,
  LANDING_PAGE_TEMPLATES
} from "../components/strings";

const colorFieldMessages = {
  invalidCode: "Invalid color code (eg: #ffffff)",
  minLength: "Must be 7 digit code"
};
const errorTextMessage = "Required";

const YupSelectFieldValidation = lazy((value) => {
  if (value) {
    switch (typeof value) {
      case "string":
        return string().required(errorTextMessage).nullable();
      case "object":
        return object().shape({
          _id: string().required(errorTextMessage).nullable()
        });
      default:
        return mixed();
    }
  } else {
    return string().required(errorTextMessage).nullable();
  }
});
const YupSelectFieldValidationForOneOf = lazy((value) => {
  if (!value) {
    return mixed().nullable();
  }
  switch (typeof value) {
    case "string":
      return string().nullable();
    case "object":
      return object().shape({
        _id: string().required(errorTextMessage).nullable()
      });
    default:
      return mixed().nullable();
  }
});

const ValidateColorPickerWithText = string()
  .required(errorTextMessage)
  .matches(colorCodeRegex, {
    message: colorFieldMessages.invalidCode,
    excludeEmptyString: false
  })
  .min(7, colorFieldMessages.minLength);

const requiredWhenPolicyDisplayEnabled = () =>
  string().when("policyDisplay", {
    is: true,
    then: (schema) => schema.required(errorTextMessage),
    otherwise: (schema) => schema.notRequired()
  });

const QACommonSchema = object().shape({
  funnelQA: array(
    object().shape({
      text: string().required(errorTextMessage),
      answers: array(
        object().shape({
          text: string().when("ansType", {
            is: (ansType) =>
              [QUESTION_TYPE.ICONS, QUESTION_TYPE.TEXTONLY].includes(ansType),
            then: (schema) => schema.required(errorTextMessage),
            otherwise: (schema) => schema.notRequired()
          }),
          asset: object()
            .shape({})
            .when("ansType", {
              is: QUESTION_TYPE.ICONS,
              then: (schema) =>
                schema.shape({}).defined(errorTextMessage).nullable(),
              otherwise: (schema) => schema.shape({}).nullable().notRequired()
            })
        })
      ).when("type", {
        is: (typeVal) =>
          typeVal !== QUESTION_TYPE.FREE_TEXT_FIELD &&
          typeVal !== QUESTION_TYPE.SLIDER,
        then: (schema) =>
          schema.min(1, "This question must have at least 1 answer*"),
        otherwise: (schema) => schema.notRequired()
      }),
      range: object()
        .shape({})
        .when("type", {
          is: QUESTION_TYPE.SLIDER,
          then: (schema) =>
            schema.shape({
              min: number().typeError(errorTextMessage),
              max: number().typeError(errorTextMessage)
              // step: number().typeError(errorTextMessage),
            }),
          otherwise: (schema) => schema.notRequired()
        })
    })
  ).min(1, "Funnels QA must have at least 1 question*")
});

// Size of the company logo in the partner badge (Asana 1213410886347834):
// empty = 100 %. Checked only while a badge mode is selected; with an own
// image a leftover invalid entry is dropped instead of blocking the save.
const partnerLogoScaleMessage = "Must be a whole number from 50 to 100";
const ValidatePartnerLogoScale = number()
  .transform((value, originalValue) =>
    originalValue === "" || originalValue === null || Number.isNaN(value)
      ? undefined
      : value
  )
  .when("partnerLogoMode", ([partnerLogoMode], schema) =>
    [PARTNER_LOGO_MODE.AUTO_LIGHT, PARTNER_LOGO_MODE.AUTO_DARK].includes(
      partnerLogoMode
    )
      ? schema
          .integer(partnerLogoScaleMessage)
          .min(PARTNER_LOGO_SCALE.MIN, partnerLogoScaleMessage)
          .max(PARTNER_LOGO_SCALE.MAX, partnerLogoScaleMessage)
      : schema.transform((value) =>
          Number.isInteger(value) &&
          value >= PARTNER_LOGO_SCALE.MIN &&
          value <= PARTNER_LOGO_SCALE.MAX
            ? value
            : undefined
        )
  )
  .notRequired();

const DesignCommonSchema = object().shape({
  design: object()
    .shape({
      partnerLogoScale: ValidatePartnerLogoScale,
      policyCheckboxText: requiredWhenPolicyDisplayEnabled(),
      policyModalHeading: requiredWhenPolicyDisplayEnabled(),
      policyModalContent: requiredWhenPolicyDisplayEnabled(),
      policyCheckboxClickableKeyword: string().when(
        ["policyDisplay", "policyCheckboxText"],
        {
          is: (policyDisplay) => policyDisplay,
          then: (schema) =>
            schema
              .required(errorTextMessage)
              .min(3, "Clickable keyword must be at least 3 characters")
              .test(
                "keyword-in-policy-text",
                "Clickable keyword must exist in the policy text",
                function (value) {
                  const { policyCheckboxText } = this.parent;
                  if (!value || !policyCheckboxText) {
                    return true; // required validators handle empties
                  }
                  return policyCheckboxText
                    .toLowerCase()
                    .includes(value.trim().toLowerCase());
                }
              ),
          otherwise: (schema) => schema.notRequired()
        }
      )
    })
    .when(
      ["funnelDesign", "templateDesign"],
      ([funnelDesign, templateDesign], schema) => {
        const designType = funnelDesign || templateDesign;

        if (designType === FUNNEL_DESIGNS.CLASSIC) {
          return schema.shape({
            baseColor: ValidateColorPickerWithText,
            companyLogoBackground: ValidateColorPickerWithText
          });
        }

        return schema.shape({
          baseColor: ValidateColorPickerWithText,
          modernBackgroundOne: ValidateColorPickerWithText,
          modernBackgroundTwo: ValidateColorPickerWithText,
          modernHeaderLabelsColor: ValidateColorPickerWithText,
          modernNextButtonTextColor: ValidateColorPickerWithText,
          modernSubmitButtonTextColor: ValidateColorPickerWithText,
          modernSubmitButtonBackground: ValidateColorPickerWithText,
          modernNextButtonBackground: ValidateColorPickerWithText,
          modernNextButtonHoverBackground: ValidateColorPickerWithText,
          modernContrastColor: ValidateColorPickerWithText,
          additionalTaglineColor: ValidateColorPickerWithText,
          additionalTaglineBackground: ValidateColorPickerWithText,
          modernHeadline: string().required(errorTextMessage),
          modernFunnelHeadline: string().required(errorTextMessage),
          modernFormHeading: string().required(errorTextMessage),
          modernFormInfoLabelOne: string().required(errorTextMessage),
          modernFormInfoLabelTwo: string().required(errorTextMessage),
          modernFormInfoLabelThree: string().required(errorTextMessage),
          additionalTaglineLabelOne: string().required(errorTextMessage),
          additionalTaglineLabelTwo: string().required(errorTextMessage),
          modernNextButtonText: string().required(errorTextMessage),
          modernSubmitButtonText: string().required(errorTextMessage),
          modernHeaderCallLabel: string().required(errorTextMessage),
          modernHeaderPhoneNumber: number()
            .typeError(errorTextMessage)
            .required(errorTextMessage),
          modernHeaderEmailAddress: string().email().required(errorTextMessage),
          uspIcons: array(
            object().shape({
              label: string().required(errorTextMessage),
              icon: string().required(errorTextMessage),
              color: ValidateColorPickerWithText
            })
          ).min(1, "USP Icons list is empty")
        });
      }
    )
});

const ThankyouCommonSchema = object().shape({
  thankyouPage: object().shape({
    redirectUrl: string().when("redirectLead", {
      is: true,
      then: (schema) => schema.required(errorTextMessage),
      otherwise: (schema) => schema.notRequired()
    }),
    addressLabel: string().when("enableAddressAfterLeadGeneration", {
      is: true,
      then: (schema) => schema.required(errorTextMessage),
      otherwise: (schema) => schema.notRequired()
    }),
    addressSubmitButtonLabel: string().when(
      "enableAddressAfterLeadGeneration",
      {
        is: true,
        then: (schema) => schema.required(errorTextMessage),
        otherwise: (schema) => schema.notRequired()
      }
    )
  })
});
const TextElementsCommonSchema = object().shape({
  textElements: object().shape({
    numberCheck: string(errorTextMessage).required(errorTextMessage),
    phoneScreenHeading: string().when("enablePhoneWithTwoSteps", {
      is: true,
      then: (schema) => schema.required(errorTextMessage),
      otherwise: (schema) => schema.notRequired()
    }),
    formImage: mixed().when("enableFormImage", {
      is: true,
      then: (schema) => schema.required(errorTextMessage).nullable(),
      otherwise: (schema) => schema.nullable().notRequired()
    })
  })
});

const GeneralFunnelSchema = object().shape({
  general: object().shape({
    funnelName: string().required(errorTextMessage),
    pricePerInvoiceUnit: number().typeError(errorTextMessage)
  }),
  customer: YupSelectFieldValidation,
  category: YupSelectFieldValidation,
  hubspotProjectId: string().required(errorTextMessage)
});

export const TemplateModalSchema = QACommonSchema.shape({
  name: string().required(errorTextMessage)
});

export const TemplateFormSchema = TemplateModalSchema.concat(DesignCommonSchema)
  .concat(TextElementsCommonSchema)
  .concat(ThankyouCommonSchema);

export const FunnelFacebookFormSchema = GeneralFunnelSchema.concat(
  object().shape({
    facebookForm: object().shape({
      forms: array(
        object()
          .shape({
            id: string().required(errorTextMessage)
          })
          .nullable()
          .required(errorTextMessage)
      ).min(1, "Facebook page must have at least 1 form*"),
      page: object()
        .shape({
          id: string().required(errorTextMessage)
        })
        .nullable()
        .required(errorTextMessage)
    })
  })
);

export const FunnelBasicFormSchema = QACommonSchema.concat(DesignCommonSchema)
  .concat(TextElementsCommonSchema)
  .concat(ThankyouCommonSchema)
  .concat(GeneralFunnelSchema)
  .concat(
    object().shape({
      subDomain: string().required(errorTextMessage)
    })
  );

// Only for creating a funnel: existing funnels keep the country they have
const FunnelCreateCountrySchema = object().shape({
  companyInfo: object().shape({
    country: string().required(errorTextMessage)
  })
});

export const FunnelCreateBasicFormSchema = FunnelBasicFormSchema.concat(
  FunnelCreateCountrySchema
);

export const FunnelCreateFacebookFormSchema = FunnelFacebookFormSchema.concat(
  FunnelCreateCountrySchema
);

export const LoginFormSchema = object().shape({
  email: string().required(errorTextMessage),
  password: string().required(errorTextMessage)
});

export const ForgotPasswordFormSchema = object().shape({
  email: string().required(errorTextMessage)
});

export const ResetPasswordFormSchema = object().shape({
  password: string().required(errorTextMessage),
  confirmPassword: string().oneOf(
    [ref("password"), null],
    "Passwords must match"
  )
});

export const ImportFunnelSchema = object().shape({
  importUrl: string().required(errorTextMessage)
});

export const CustomerFormSchema = object().shape({
  firstName: string().required(errorTextMessage).nullable(),
  lastName: string().required(errorTextMessage).nullable(),
  email: string().email().required(errorTextMessage)
});

export const CategoryFormSchema = object().shape({
  name: string().required(errorTextMessage).nullable(),
  leadPriceBenchmark: number()
    .typeError(errorTextMessage)
    .required(errorTextMessage),
  leadQualifiedPriceBenchmark: number()
    .typeError(errorTextMessage)
    .required(errorTextMessage)
});

export const KeywordFormSchema = object().shape({
  name: string().required(errorTextMessage).nullable()
});

export const TagFormSchema = object().shape({
  name: string().required(errorTextMessage).nullable()
});

export const DomainDialogSchema = object().shape({
  domain: string().required(errorTextMessage),
  subDomain: string().required(errorTextMessage)
});

export const IntegrationFormSchema = object().shape({
  webHookUrl: string().required(errorTextMessage)
});

export const UserCommonSchema = object().shape({
  firstName: string().required(errorTextMessage),
  lastName: string().required(errorTextMessage),
  status: string().required(errorTextMessage),
  role: string().required(errorTextMessage)
});

export const CreateUserSchema = UserCommonSchema.concat(
  object().shape({
    email: string().required(errorTextMessage),
    password: string().required(errorTextMessage)
  })
);

export const ChangePasswordFormSchema = object().shape({
  oldPassword: string().required(errorTextMessage),
  newPassword: string().required(errorTextMessage),
  cNewPassword: string()
    .required(errorTextMessage)
    .oneOf([ref("newPassword"), null], "Passwords must match")
});
export const LeadUpdateSchema = object().shape({
  leadEmail: string().email().required(errorTextMessage),
  leadPhone: string().required(errorTextMessage),
  leadName: string().required(errorTextMessage)
});

// The value stays the wall clock the datetime-local input holds
// ("YYYY-MM-DDTHH:mm", German time). It must not become a Date here: yup
// would read it in the PC's timezone.
export const LeadAppointmentUpdateSchema = object().shape({
  appointmentOn: string()
    .required(errorTextMessage)
    .test("businessTime", errorTextMessage, (value) => {
      try {
        return Boolean(localToInstant(value));
      } catch (error) {
        return false;
      }
    })
});

export const FunnelFiltersFormSchema = object().shape({
  heading: string().required(errorTextMessage),
  message: string().required(errorTextMessage)
});

export const AdCopyScehma = object().shape({
  companyName: string().required(errorTextMessage),
  category: YupSelectFieldValidation,
  funnelUrl: string().required(errorTextMessage)
});

export const AdDuplicatorScehma = object().shape({
  viewId: string().required(errorTextMessage)
});

const ContactCloudSelectSchema = (filter) =>
  filter
    ? object()
        .shape()
        .when("for", {
          is: filter,
          then: (schema) =>
            schema.shape({
              id: string().required(errorTextMessage).nullable()
            }),
          otherwise: (schema) => schema.shape({}).notRequired()
        })
    : object().shape({
        id: string().required(errorTextMessage).nullable()
      });

export const ContactCloudSetupSchema = object().shape({
  userId: ContactCloudSelectSchema(),
  productId: ContactCloudSelectSchema(FUNNEL_TYPE_FILTER.LEAD),
  jobId: ContactCloudSelectSchema(FUNNEL_TYPE_FILTER.EMPLOYEE)
});

export const EmailIntegrationFormSchema = object().shape({
  email: string().required(errorTextMessage)
});

// Recipients of the appointment emails (Asana 1213561761053876): checked only
// with confirmation "YES"; same rule as the backend (dot in the domain part)
const appointmentEmailMessages = {
  invalid: "Invalid email",
  duplicate: "Email already entered"
};
const appointmentEmailField = (previousFields = []) =>
  string()
    .trim()
    .when("appointmentConfirmation", {
      is: "YES",
      then: (schema) =>
        schema
          .email(appointmentEmailMessages.invalid)
          .matches(/^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/, {
            message: appointmentEmailMessages.invalid,
            excludeEmptyString: true
          })
          .test(
            "unique-appointment-email",
            appointmentEmailMessages.duplicate,
            function (value) {
              const email = value?.toLowerCase();
              return (
                !email ||
                !previousFields.some(
                  (field) => this.parent[field]?.trim().toLowerCase() === email
                )
              );
            }
          ),
      otherwise: (schema) => schema.notRequired()
    });

export const AppointmentFormSchema = object().shape({
  appointmentLink: string().required(errorTextMessage),
  appointmentConfirmation: string().required(errorTextMessage),
  appointmentType: string().required(errorTextMessage).nullable(),
  appointmentEmail1: appointmentEmailField().when("appointmentConfirmation", {
    is: "YES",
    then: (schema) => schema.required(errorTextMessage)
  }),
  appointmentEmail2: appointmentEmailField(["appointmentEmail1"]),
  appointmentEmail3: appointmentEmailField([
    "appointmentEmail1",
    "appointmentEmail2"
  ])
});

export const LeadServiceCompanyNameSchema = object().shape({
  companyNameForLeadAgent: string().when(["enabled", "enabledPlus"], {
    is: (enabled, enabledPlus) => enabled || enabledPlus,
    then: (schema) => schema.required(errorTextMessage),
    otherwise: (schema) => schema.notRequired()
  })
});

export const GalleryFormSchema = object()
  .shape({
    category: YupSelectFieldValidationForOneOf,
    keyword: YupSelectFieldValidationForOneOf
  })
  .test(
    "category-or-keyword",
    "Either category or keyword is required",
    function (values) {
      const hasCategory =
        typeof values?.category === "string"
          ? values.category?.trim()
          : values?.category?._id;
      const hasKeyword =
        typeof values?.keyword === "string"
          ? values.keyword?.trim()
          : values?.keyword?._id;
      if (!hasCategory && !hasKeyword) {
        return this.createError({
          path: "category", // show on only one, clean UX
          message: errorTextMessage
        });
      }
      return true;
    }
  )
  .test(
    "category-or-keyword",
    "Either category or keyword is required",
    function (values) {
      const hasCategory =
        typeof values?.category === "string"
          ? values.category?.trim()
          : values?.category?._id;
      const hasKeyword =
        typeof values?.keyword === "string"
          ? values.keyword?.trim()
          : values?.keyword?._id;
      if (!hasCategory && !hasKeyword) {
        return this.createError({
          path: "keyword", // show on only one, clean UX
          message: errorTextMessage
        });
      }
      return true;
    }
  );

export const SuggestionGallerySchema = object().shape({
  link: string().required(errorTextMessage)
});

export const TestedLinksSchema = object().shape({
  linkToCampaign: string().required(errorTextMessage),
  linkToFunnel: string().required(errorTextMessage),
  category: YupSelectFieldValidation
});

export const AdvertisingAccountLinkFormSchema = object().shape({
  accountLink: string().required(errorTextMessage)
});

export const ContactLaterDialogSchema = object().shape({
  date: date().typeError(errorTextMessage).required()
});

// export const AppointmentDateDialogSchema = object().shape({
//   date: date().typeError(errorTextMessage).required(),
//   appointmentType: string().required(errorTextMessage)
// });
export const AppointmentDateDialogSchema = (forls, errorTextMessage) =>
  object().shape({
    date: date().typeError(errorTextMessage).required(),
    appointmentType: forls
      ? string().required(errorTextMessage)
      : string().notRequired()
  });

export const LandingPageTemplateModalSchema = object().shape({
  name: string().required(errorTextMessage)
});

const CommonLandingPageSchema = object().shape({
  headlineText: string().required(errorTextMessage),
  headlineColor: ValidateColorPickerWithText,
  heroImage: mixed().required(errorTextMessage).nullable(),
  heroContent: string().required(errorTextMessage),
  ctaButtonText: string().required(errorTextMessage),
  ctaButtonColor: ValidateColorPickerWithText,
  ctaButtonBackgroundColor: ValidateColorPickerWithText,
  benefitsGridHeadline: string().required(errorTextMessage),
  benefitsGridTextColor: ValidateColorPickerWithText,
  benefitsGridHeadlineColor: ValidateColorPickerWithText
});

const LANDING_PAGE_TEMPLATE_SCHEMAS = {
  [LANDING_PAGE_TEMPLATES.FUNNEL_LANDING_PAGE_TEMP_ONE]: object({
    heroHeadline: string().required(errorTextMessage),
    heroHeadlineColor: ValidateColorPickerWithText,
    headerBackgroundColor: string().required(errorTextMessage),
    footerContent: string().required(errorTextMessage),
    benefitsGridSubHeadline: string().required(errorTextMessage),
    benefitsGrid: array()
      .of(
        object({
          label: string().required(errorTextMessage),
          subText: string().required(errorTextMessage),
          icon: string().required(errorTextMessage)
        })
      )
      .min(1, "Benefits grid are empty")
  }),

  [LANDING_PAGE_TEMPLATES.FUNNEL_LANDING_PAGE_TEMP_TWO]: object({
    backgroundImage: mixed().required(errorTextMessage).nullable(),
    subHeadlineText: string().required(errorTextMessage),
    subHeadlineColor: ValidateColorPickerWithText,
    centerContentBackgroundColor: ValidateColorPickerWithText,
    centerContentBackgroundOpacity: string().required(errorTextMessage),
    contactUsButtonText: string().required(errorTextMessage),
    contactUsButtonColor: ValidateColorPickerWithText,
    contactUsButtonHoverBackground: ValidateColorPickerWithText,
    phoneNumberHeadline: string().required(errorTextMessage),
    phoneNumberText: string().required(errorTextMessage),
    phoneNumberColor: ValidateColorPickerWithText,
    benefitsGrid: array()
      .of(
        object({
          label: string().required(errorTextMessage),
          icon: string().required(errorTextMessage)
        })
      )
      .min(1, "Benefits grid are empty"),
    heroContentPoints: array()
      .of(
        object({
          label: string().required(errorTextMessage),
          text: string().required(errorTextMessage)
        })
      )
      .min(1, "Hero content points are empty"),
    footerForeground: string().required(errorTextMessage),
    footerGoogleMapIframeUrl: string().required(errorTextMessage),
    heroContentTextColor: ValidateColorPickerWithText,
    heroContentIconColor: ValidateColorPickerWithText
  })
};

export const LandingPageSchema = object({
  template: YupSelectFieldValidation,
  data: object().when(["template"], (template) => {
    const templateId = template[0]?._id;
    // Base schema
    let baseSchema = CommonLandingPageSchema;
    // Get the matching template schema
    const templateSchema = LANDING_PAGE_TEMPLATE_SCHEMAS[templateId];
    // Merge it if it exists, otherwise just return base schema
    return templateSchema ? baseSchema.concat(templateSchema) : baseSchema;
  })
});

export const DuplicateLandingPageSchema = object().shape({
  selectedFunnel: YupSelectFieldValidation
});

export const PrivacyConsentModalSchema = object().shape({
  checkboxText: string().required(errorTextMessage),
  checkboxClickableKeyword: string().required(errorTextMessage),
  modalHeading: string().required(errorTextMessage),
  modalContent: string().required(errorTextMessage)
});
